/**
 * SurveyCorrections — Plan of Correction tracking per finding.
 *
 * Shows findings that need corrective action. Surveyor can create POC
 * entries, set deadlines, and verify facility submissions.
 *
 * Route: /visits/:visitId/survey/corrections
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Plus, CheckCircle, Clock, X } from 'lucide-react'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { getSurvey, type SurveySession } from '../../lib/services/survey'
import { surveyStore, type PendingFinding } from '../../lib/offline/surveyStore'
import {
  getCorrections,
  createCorrection,
  updateCorrection,
  type SurveyCorrection,
  type CorrectionStatus,
} from '../../lib/services/corrections'

export function SurveyCorrections() {
  const { visitId } = useParams<{ visitId: string }>()
  const [session, setSession] = useState<SurveySession | null>(null)
  const [findings, setFindings] = useState<PendingFinding[]>([])
  const [corrections, setCorrections] = useState<SurveyCorrection[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [selectedFinding, setSelectedFinding] = useState<string | null>(null)
  const [deadline, setDeadline] = useState('')
  const [responsibleParty, setResponsibleParty] = useState('')

  useEffect(() => {
    if (!visitId) return
    const load = async () => {
      setIsLoading(true)
      const s = await getSurvey(visitId)
        .then((s) => s ?? surveyStore.getSessionByVisit(visitId).then((os) => os ?? null))
        .catch(() => surveyStore.getSessionByVisit(visitId).then((os) => os ?? null))
      if (!s) { setIsLoading(false); return }
      setSession(s)
      const [f, c] = await Promise.all([
        surveyStore.getFindingsBySurvey(s.survey_id),
        getCorrections(visitId, s.survey_id).catch(() => []),
      ])
      setFindings(f)
      setCorrections(c)
      setIsLoading(false)
    }
    load()
  }, [visitId])

  const handleCreate = useCallback(async () => {
    if (!session || !visitId || !selectedFinding) return
    try {
      const corr = await createCorrection(visitId, session.survey_id, selectedFinding, {
        deadline: deadline || undefined,
        responsible_party: responsibleParty || undefined,
      })
      setCorrections((prev) => [...prev, corr])
      setShowCreate(false)
      setSelectedFinding(null)
      setDeadline('')
      setResponsibleParty('')
    } catch {
      // Error handled silently — API may not be deployed yet
    }
  }, [session, visitId, selectedFinding, deadline, responsibleParty])

  const handleVerify = useCallback(async (correctionId: string) => {
    if (!session || !visitId) return
    const notes = prompt('Verification notes:')
    if (notes === null) return
    try {
      const updated = await updateCorrection(visitId, session.survey_id, correctionId, {
        status: 'verified',
        verifier_notes: notes,
      })
      setCorrections((prev) => prev.map((c) => c.correction_id === correctionId ? updated : c))
    } catch {
      // Error handled silently
    }
  }, [session, visitId])

  if (isLoading) {
    return (
      <FieldAppShell eyebrow="Survey" title="Plans of Correction" backTo=".." backLabel="Back">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--brand-primary)]" />
        </div>
      </FieldAppShell>
    )
  }

  if (!session) {
    return (
      <FieldAppShell eyebrow="Survey" title="Plans of Correction" backTo=".." backLabel="Back">
        <div className="px-4 py-6">
          <p className="text-sm text-slate-500">No survey found.</p>
          <Link to=".." className="btn-ghost text-sm mt-4 inline-block">Back</Link>
        </div>
      </FieldAppShell>
    )
  }

  const findingsWithoutPoc = findings.filter(
    (f) => f.severity >= 2 && !corrections.some((c) => c.finding_id === f.finding_id),
  )

  const statusColors: Record<CorrectionStatus, string> = {
    pending: 'bg-slate-100 text-slate-600',
    submitted: 'bg-blue-100 text-blue-700',
    accepted: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    verified: 'bg-emerald-100 text-emerald-700',
    overdue: 'bg-red-100 text-red-700',
  }

  return (
    <FieldAppShell eyebrow="Survey" title="Plans of Correction" backTo=".." backLabel="Back">
    <div className="space-y-4 px-4 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link to=".." className="text-slate-400 hover:text-slate-600">
            <ArrowLeft size={20} />
          </Link>
          <h2 className="section-title">Plans of Correction</h2>
        </div>
      </div>

      {/* Summary */}
      <div className="app-surface p-4 grid grid-cols-3 gap-3 text-center text-sm">
        <div>
          <p className="text-2xl font-bold text-amber-500">{findingsWithoutPoc.length}</p>
          <p className="text-[10px] text-slate-500">Need POC</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-blue-500">{corrections.filter((c) => c.status === 'submitted').length}</p>
          <p className="text-[10px] text-slate-500">Submitted</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-emerald-500">{corrections.filter((c) => c.status === 'verified').length}</p>
          <p className="text-[10px] text-slate-500">Verified</p>
        </div>
      </div>

      {/* Create POC */}
      {!showCreate && findingsWithoutPoc.length > 0 && (
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="btn-primary w-full"
        >
          <Plus size={14} className="inline mr-1" /> Create POC for Finding
        </button>
      )}

      {showCreate && (
        <div className="app-surface p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="section-title text-base">New Plan of Correction</span>
            <button type="button" onClick={() => setShowCreate(false)} className="text-slate-400"><X size={16} /></button>
          </div>

          <select
            value={selectedFinding ?? ''}
            onChange={(e) => setSelectedFinding(e.target.value || null)}
            className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          >
            <option value="">Select a finding...</option>
            {findingsWithoutPoc.map((f) => (
              <option key={f.finding_id} value={f.finding_id}>
                [{f.domain}] {f.observation.slice(0, 60)}...
              </option>
            ))}
          </select>

          <input
            type="text"
            placeholder="Responsible party"
            value={responsibleParty}
            onChange={(e) => setResponsibleParty(e.target.value)}
            className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          />

          <input
            type="date"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
          />

          <button
            type="button"
            onClick={handleCreate}
            disabled={!selectedFinding}
            className="btn-primary w-full py-2.5 text-sm disabled:opacity-60"
          >
            Create POC
          </button>
        </div>
      )}

      {/* Existing corrections */}
      <div className="space-y-2">
        {corrections.map((corr) => {
          const finding = findings.find((f) => f.finding_id === corr.finding_id)
          return (
            <div key={corr.correction_id} className="app-surface p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="brand-chip text-[10px]">{finding?.domain ?? '—'}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusColors[corr.status]}`}>
                  {corr.status}
                </span>
              </div>
              <p className="text-xs text-slate-700 line-clamp-2">{finding?.observation ?? 'Finding not found'}</p>
              {corr.responsible_party && (
                <p className="text-[10px] text-slate-500">Responsible: {corr.responsible_party}</p>
              )}
              {corr.deadline && (
                <p className="text-[10px] text-slate-500 flex items-center gap-1">
                  <Clock size={10} /> Due: {new Date(corr.deadline).toLocaleDateString()}
                </p>
              )}
              {corr.correction_plan && (
                <p className="text-[10px] text-slate-600 bg-slate-50 rounded p-2">{corr.correction_plan}</p>
              )}
              {corr.status === 'submitted' && (
                <button
                  type="button"
                  onClick={() => handleVerify(corr.correction_id)}
                  className="w-full py-1.5 rounded-lg text-[11px] font-bold bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                >
                  <CheckCircle size={12} className="inline mr-1" /> Verify Correction
                </button>
              )}
              {corr.verifier_notes && (
                <p className="text-[10px] text-emerald-600 italic">Verified: {corr.verifier_notes}</p>
              )}
            </div>
          )
        })}
      </div>

      {corrections.length === 0 && !showCreate && (
        <p className="text-center text-sm text-slate-400 py-8">
          {findingsWithoutPoc.length > 0
            ? `${findingsWithoutPoc.length} finding(s) with severity Moderate or High need Plans of Correction.`
            : 'No findings require Plans of Correction.'}
        </p>
      )}
    </div>
    </FieldAppShell>
  )
}
